<template>
  <div class="flex flex-wrap gap-2">
    <label
      v-for="(opt, index) in options"
      :key="index"
      v-tooltip
      :title="opt.tooltip"
      :class="['radio-button', sizeClass, { 'selected': selected === opt.value, 'cursor-not-allowed pointer-events-none disabled': disabled }]"
    >
      <input
        type="radio"
        v-model="selected"
        :value="opt.value"
        :disabled="disabled"
        class="hidden"
        />
      <UiIcon v-if="!hideRadio" :icon="selected === opt.value ? 'circle-check-filled' : 'circle'" />
      <span>{{ opt.label }}</span>
      <UiBadge v-if="opt.badge !== undefined && opt.badge !== null" type="primary" variant="solid" size="sm" class="rounded-full min-w-[20px] px-1.5 py-0.5 inline-flex items-center justify-center text-xs font-semibold shrink-0">
        {{ opt.badge }}
      </UiBadge>
      <slot v-if="hasSuffix" name="suffix" :option="opt" />
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import UiIcon from './icon.vue'
import UiBadge from './badge.vue'

const slots = useSlots()

const props = defineProps<{
  modelValue: any
  options: {
    label: string
    value: any
    tooltip?: string
    badge?: number | string
  }[]
  disabled?: boolean
  hideRadio?: boolean
  size?: 'sm' | 'md' | 'lg'
}>()

const hasSuffix = computed(() => !!slots.suffix || false)

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'py-2 px-3';
    case 'lg':
      return 'py-4 px-8';
    case 'md':
    default:
      return 'py-3 px-6';
  }
});

const emit = defineEmits(['update:modelValue'])

const selected = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})
</script>

<style scoped lang="scss">
.radio-button {
  @apply inline-flex items-center gap-1 box-border border border-solid text-sm bg-transparent transition-colors rounded-lg text-neutral-500 dark:text-neutral-200 cursor-pointer;

  > input[type="radio"] {
    @apply m-0 checked:accent-[--c-primary-900] dark:checked:accent-[--c-primary-100];
  }

  > span {
    @apply inline-block;
  }

  &.selected {
    @apply border-[--c-primary-600] bg-[--c-primary-600];
    text-shadow: .25px 0px .1px, -.25px 0px .1px;

    > span, > svg {
      @apply text-[--c-primary-at-900] dark:text-neutral-100;
    }

    &.disabled {
      @apply border-[--c-primary-400] bg-[--c-primary-400] grayscale;
    }
  }

  &:not(.selected) {
    @apply border-neutral-700/30 dark:border-neutral-100/30;
  }
}
</style>
